import { addMinutes, compareAsc } from 'date-fns'
import { useEffect, useState } from 'react'
import sanitize from 'sanitize-html'

import styles from './TimeSlider.module.scss'

interface ITimeSlider {
  step: number
  type: 'hours' | 'minutes'
  value?: Date
  onChange: (newValue: Date) => void
  fromDate?: Date
  toDate?: Date
}

export const TimeSlider = ({ step, value, type, onChange, fromDate, toDate }: ITimeSlider) => {
  const [disabledAddArrow, setDisabledAddArrow] = useState<boolean>(!value)
  const [disabledSubtractionArrow, setDisabledSubtractionArrow] = useState<boolean>(!value)

  const changeValue = (action: 'plus' | 'minus') => {
    if (value) {
      const delta = action === 'plus' ? 1 : -1
      const newValue = addMinutes(value, step * delta)
      onChange(newValue)
    }
  }

  useEffect(() => {
    if (value) {
      if (fromDate && compareAsc(value, fromDate) < 0) {
        console.error(
          sanitize(
            `TimeSlider: value (${value.toISOString()}) is before fromDate (${fromDate.toISOString()})`
          )
        )
      }
      if (toDate && compareAsc(value, toDate) > 0) {
        console.error(
          sanitize(
            `TimeSlider: value (${value.toISOString()}) is after toDate (${toDate.toISOString()})`
          )
        )
      }

      const nextValue = addMinutes(value, step)
      const nextValueDay = nextValue.getDate()
      const isNextClickable =
        nextValueDay - value.getDate() !== 1 && (!toDate || compareAsc(nextValue, toDate) <= 0)
      setDisabledAddArrow(!isNextClickable)

      const prevValue = addMinutes(value, step * -1)
      const prevValueDay = prevValue.getDate()
      const isPrevClickable =
        value.getDate() - prevValueDay !== 1 && (!fromDate || compareAsc(prevValue, fromDate) >= 0)
      setDisabledSubtractionArrow(!isPrevClickable)
    }
  }, [value, fromDate, toDate, step])

  const getRenderValue = () => {
    if (!value) {
      return 0
    }
    if (type === 'hours') {
      return value.getHours()
    }
    return value.getMinutes()
  }

  return (
    <div className={styles.root}>
      <div
        data-testid={`time-slider__${type}-up`}
        className={`kbase kbase-omni-chevron-up ${
          disabledAddArrow ? styles.arrow_disabled : styles.arrow
        }`}
        onClick={() => {
          if (!disabledAddArrow) {
            changeValue('plus')
          }
        }}
      />
      <div>{getRenderValue()}</div>
      <div
        data-testid={`time-slider__${type}-down`}
        className={`kbase kbase-omni-chevron-down ${
          disabledSubtractionArrow ? styles.arrow_disabled : styles.arrow
        }`}
        onClick={() => {
          if (!disabledSubtractionArrow) {
            changeValue('minus')
          }
        }}
      />
    </div>
  )
}
